import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  FileUpload,
  FormField,
  Modal,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { Stage } from "../../lusca-search-api/generated-src";
import { useImportTrackStarsReport } from "../../hooks/useInvestigations";

// Mirrors the backend cap (MAX_IMPORT_REPORT_BYTES); checked here so a huge file fails fast.
export const MAX_REPORT_BYTES = 5 * 1024 * 1024;

export interface ParsedReport {
  report: Record<string, unknown>;
  reportId: string;
  generatedAt?: string;
  startDate?: string;
  endDate?: string;
  patterns: number;
}

/** Read an uploaded file as a TrackStars report, or explain why it is not one. */
export const parseReport = (
  text: string
): { parsed?: ParsedReport; error?: string } => {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    return { error: "This file is not valid JSON." };
  }
  if (!json || typeof json !== "object" || Array.isArray(json))
    return { error: "This file is not a TrackStars report." };
  const report = json as Record<string, unknown>;
  const metadata = (report.metadata ?? {}) as Record<string, unknown>;
  const range = (metadata.date_range ?? {}) as Record<string, unknown>;
  const patterns = report.pattern_analysis;
  if (typeof metadata.report_id !== "string" || !metadata.report_id)
    return { error: "This file has no metadata.report_id." };
  if (!Array.isArray(patterns) || patterns.length === 0)
    return { error: "This report has no patterns (pattern_analysis)." };
  return {
    parsed: {
      report,
      reportId: metadata.report_id,
      generatedAt:
        typeof metadata.generated_at === "string"
          ? metadata.generated_at
          : undefined,
      startDate:
        typeof range.start_date === "string" ? range.start_date : undefined,
      endDate: typeof range.end_date === "string" ? range.end_date : undefined,
      patterns: patterns.length,
    },
  };
};

interface TrackStarsReportUploadProps {
  stage: Stage;
  /** The stage's current report, so the dialog can say what the upload replaces. */
  currentReportId?: string;
}

/** Mounted only while open, so the mutation exists only for an actual upload. */
const UploadModal: React.FC<
  TrackStarsReportUploadProps & { onClose: () => void }
> = ({ stage, currentReportId, onClose }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [parsed, setParsed] = useState<ParsedReport>();
  const [fileError, setFileError] = useState<string>();
  const upload = useImportTrackStarsReport(stage);

  const close = onClose;

  const choose = (chosen: File[]) => {
    setFiles(chosen);
    setParsed(undefined);
    setFileError(undefined);
    upload.reset();
    const file = chosen[0];
    if (!file) return;
    if (file.size > MAX_REPORT_BYTES) {
      setFileError("The report must be at most 5 MB.");
      return;
    }
    file
      .text()
      .then((text) => {
        const result = parseReport(text);
        setParsed(result.parsed);
        setFileError(result.error);
      })
      .catch(() => setFileError("The file could not be read."));
  };

  const submit = () => {
    if (!parsed) return;
    upload.mutateAsync(parsed.report).then(close).catch(console.error);
  };

  return (
    <Modal
      visible
      onDismiss={close}
      header={`Upload a TrackStars report (${stage})`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!parsed}
              loading={upload.isLoading}
              onClick={submit}
            >
              Upload
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField
          label="Report file"
          description="The JSON report TrackStars produced."
        >
          <FileUpload
            accept="application/json,.json"
            value={files}
            onChange={({ detail }) => choose(detail.value)}
            fileErrors={files.length ? [fileError ?? null] : []}
            i18nStrings={{
              uploadButtonText: (multiple) =>
                multiple ? "Choose files" : "Choose file",
              removeFileAriaLabel: (_i, name) => `Remove ${name}`,
              errorIconAriaLabel: "Error",
            }}
            constraintText="JSON, at most 5 MB."
          />
        </FormField>
        {parsed && (
          <Box data-testid="trackstars-upload-summary">
            <code>{parsed.reportId}</code>: {parsed.patterns} pattern
            {parsed.patterns === 1 ? "" : "s"}
            {parsed.startDate && parsed.endDate
              ? `, ${parsed.startDate} → ${parsed.endDate}`
              : ""}
            .
            {currentReportId && currentReportId !== parsed.reportId && (
              <Box color="text-body-secondary" fontSize="body-s">
                Replaces <code>{currentReportId}</code> as the latest report.
                Cases opened from it keep their pattern.
              </Box>
            )}
          </Box>
        )}
        {upload.error && <Alert type="error">{upload.error.message}</Alert>}
      </SpaceBetween>
    </Modal>
  );
};

/** "Upload report…": make a TrackStars report JSON the stage's latest (drives the suggestions). */
export const TrackStarsReportUpload: React.FC<TrackStarsReportUploadProps> = (
  props
) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>Upload report…</Button>
      {open && <UploadModal {...props} onClose={() => setOpen(false)} />}
    </>
  );
};

export default TrackStarsReportUpload;
